import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrganizations } from '../../hooks/useOrganizations';
import { StakeholderRoadmapProps, ViewMode, QuarterFilter } from './types';
import { useRoadmapData } from './hooks/useRoadmapData';
import { useRoadmapFilters } from './hooks/useRoadmapFilters';
import {
  getKpiCounts,
  filterByQuarter,
  filterByKpiStatus,
} from './utils/roadmapUtils';
import { KpiBar } from './components/KpiBar';
import { HorizonView } from './components/HorizonView';
import { TimelineView } from './components/TimelineView';
import { ListView } from './components/ListView';
import { ProjectDetailPanel } from './components/ProjectDetailPanel';
import './StakeholderRoadmap.css';

const QUARTER_PILLS: { label: string; value: QuarterFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Q1', value: 'Q1' },
  { label: 'Q2', value: 'Q2' },
  { label: 'Q3', value: 'Q3' },
  { label: 'Q4', value: 'Q4' },
];

// ─── Skeleton loader ──────────────────────────────────────────────────────────

const SkeletonLoader: React.FC = () => (
  <>
    <div className="skeleton-kpi-bar">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="skeleton-kpi-card skeleton-bar" />
      ))}
    </div>
    <div className="skeleton-content">
      {[1, 2, 3, 4, 5].map(i => (
        <div key={i} className="skeleton-row skeleton-bar" />
      ))}
    </div>
  </>
);

// ─── Main component ───────────────────────────────────────────────────────────

export const StakeholderRoadmap: React.FC<StakeholderRoadmapProps> = ({
  onProjectsLoaded,
  tasks: externalTasks,
  isEmbedded = false,
}) => {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  
  // Initialize useOrganizations to set global PAT and field mappings for direct URL access
  // Must wait for this to finish before fetching data (PAT is required for Asana API calls)
  const { loading: orgsLoading } = useOrganizations();

  // ── View state — default to Horizon (the stakeholder-first view) ──
  const [viewMode, setViewMode] = useState<ViewMode>('horizon');
  const [quarterFilter, setQuarterFilter] = useState<QuarterFilter>('all');
  const [kpiFilter, setKpiFilter] = useState<'active' | 'queued' | 'completed' | 'onHold' | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProjectGid, setSelectedProjectGid] = useState<string | null>(null);

  // ── Data — only fetch after organizations (PAT) are loaded ──
  const { projects, isLoading: dataLoading, error, handleSync, asanaProjectId } = useRoadmapData({
    orgId: orgsLoading ? undefined : orgId, // Don't pass orgId until PAT is ready
    onProjectsLoaded,
    externalTasks,
  });

  const isLoading = orgsLoading || dataLoading;

  // ── Filtering pipeline ──
  // 1. Apply text/program/status/manager filters from the filter hook
  const { filteredProjects: hookFilteredProjects, updateFilter, filterOptions, toggleFilterValue, filters: activeFilters } = useRoadmapFilters({ projects });

  // Sync search query into the filter hook
  useEffect(() => {
    updateFilter('searchQuery', searchQuery);
  }, [searchQuery, updateFilter]);

  // 2. Apply KPI status filter
  const kpiFilteredProjects = useMemo(
    () => filterByKpiStatus(hookFilteredProjects, kpiFilter as any),
    [hookFilteredProjects, kpiFilter]
  );

  // 3. Apply quarter filter
  const finalProjects = useMemo(
    () => filterByQuarter(kpiFilteredProjects, quarterFilter),
    [kpiFilteredProjects, quarterFilter]
  );

  // ── Debug logging — fires whenever the project list changes ──
  useEffect(() => {
    if (projects.length === 0) return;
    console.group('[Roadmap] StakeholderRoadmap — projects received');
    console.log(`  Total projects: ${projects.length} | isEmbedded: ${isEmbedded}`);
    const withDate = projects.filter(p => p.launch_date);
    const withoutDate = projects.filter(p => !p.launch_date);
    console.log(`  With launch_date: ${withDate.length} | Without: ${withoutDate.length}`);
    if (withDate.length > 0) {
      console.log('  Sample launch_dates:', withDate.slice(0, 5).map(p => `"${p.name}" → ${p.launch_date}`));
    }
    if (withoutDate.length > 0) {
      console.log('  Missing launch_date:', withoutDate.slice(0, 5).map(p => `"${p.name}" (program=${p.program})`));
    }
    console.groupEnd();
  }, [projects, isEmbedded]);

  // ── KPI counts (always from the full project list, not filtered) ──
  const kpiCounts = useMemo(() => getKpiCounts(projects), [projects]);

  // ── Selected project ──
  const selectedProject = useMemo(
    () => projects.find(p => p.gid === selectedProjectGid) ?? null,
    [projects, selectedProjectGid]
  );

  // ── Handlers ──
  const handleProjectClick = useCallback((gid: string) => {
    setSelectedProjectGid(gid);
  }, []);

  const handlePanelClose = useCallback(() => {
    setSelectedProjectGid(null);
  }, []);

  const handleKpiFilterChange = useCallback(
    (filter: 'active' | 'queued' | 'completed' | 'onHold' | null) => {
      setKpiFilter(filter);
    },
    []
  );

  // Auto-switch to horizon view on narrow screens
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e: MediaQueryListEvent) => {
      if (e.matches) setViewMode('horizon');
    };
    if (mq.matches) setViewMode('horizon');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const rootClass = `stakeholder-roadmap${isEmbedded ? ' is-embedded' : ''}`;

  // ── Loading state ──
  if (isLoading) {
    return (
      <div className={rootClass}>
        <SkeletonLoader />
      </div>
    );
  }

  // ── Error state ──
  if (error) {
    return (
      <div className={rootClass}>
        <div className="error-state">
          <h2>Unable to load roadmap</h2>
          <p>{error}</p>
          <button onClick={handleSync} className="btn-primary" type="button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={rootClass}>
      {/* Back nav (standalone mode only) */}
      {!isEmbedded && (
        <div className="roadmap-nav">
          <button
            onClick={() => navigate(`/admin/product/${orgId}`)}
            className="btn-back"
            type="button"
          >
            ← Back to Task Prioritization
          </button>
        </div>
      )}

      {/* ── Zone 1: Hero KPI Bar (sticky) ── */}
      <KpiBar
        counts={kpiCounts}
        activeFilter={kpiFilter}
        onFilterChange={handleKpiFilterChange}
        totalProjects={projects.length}
      />

      {/* ── Zone 2: Filter Rail ── */}
      <div className="filter-rail">
        {/* Search */}
        <div className="search-wrapper">
          <span className="search-icon" aria-hidden="true">🔍</span>
          <input
            className="search-input"
            type="search"
            placeholder="Search projects…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            aria-label="Search projects"
          />
        </div>

        {/* Program filter */}
        {filterOptions.programs.length > 0 && (
          <select
            className="filter-select"
            value={activeFilters.programs[0] || ''}
            onChange={e => updateFilter('programs', e.target.value ? [e.target.value] : [])}
            aria-label="Filter by program"
          >
            <option value="">All Programs</option>
            {filterOptions.programs.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        )}

        {/* Status filter */}
        {filterOptions.statuses.length > 0 && (
          <select
            className="filter-select"
            value={activeFilters.statuses[0] || ''}
            onChange={e => updateFilter('statuses', e.target.value ? [e.target.value] : [])}
            aria-label="Filter by status"
          >
            <option value="">All Statuses</option>
            {filterOptions.statuses.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        )}

        {/* L7 Manager filter */}
        {filterOptions.l7Managers.length > 0 && (
          <select
            className="filter-select"
            value={activeFilters.l7Managers[0] || ''}
            onChange={e => updateFilter('l7Managers', e.target.value ? [e.target.value] : [])}
            aria-label="Filter by L7 manager"
          >
            <option value="">All L7 Managers</option>
            {filterOptions.l7Managers.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        )}

        {/* Assignee filter */}
        {filterOptions.assignees.length > 0 && (
          <select
            className="filter-select"
            value={activeFilters.assignees[0] || ''}
            onChange={e => updateFilter('assignees', e.target.value ? [e.target.value] : [])}
            aria-label="Filter by assignee"
          >
            <option value="">All Assignees</option>
            {filterOptions.assignees.map(a => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        )}

        {/* OP Decision filter */}
        {filterOptions.opDecisions.length > 0 && (
          <select
            className="filter-select"
            value={activeFilters.opDecisions[0] || ''}
            onChange={e => updateFilter('opDecisions', e.target.value ? [e.target.value] : [])}
            aria-label="Filter by OP decision"
          >
            <option value="">All OP Decisions</option>
            {filterOptions.opDecisions.map(o => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        )}

        {/* Target Year filter */}
        {filterOptions.missionFits.length > 0 && (
          <select
            className="filter-select"
            value={activeFilters.missionFits[0] || ''}
            onChange={e => updateFilter('missionFits', e.target.value ? [e.target.value] : [])}
            aria-label="Filter by mission fit"
          >
            <option value="">All Mission Fits</option>
            {filterOptions.missionFits.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        )}

        {/* Target Year filter */}
        {filterOptions.targetYears.length > 0 && (
          <select
            className="filter-select"
            value={activeFilters.targetYears[0] || ''}
            onChange={e => updateFilter('targetYears', e.target.value ? [e.target.value] : [])}
            aria-label="Filter by target year"
          >
            <option value="">All Years</option>
            {filterOptions.targetYears.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        )}

        {/* Request Type filter */}
        {filterOptions.requestTypes.length > 0 && (
          <select
            className="filter-select"
            value={activeFilters.requestTypes[0] || ''}
            onChange={e => updateFilter('requestTypes', e.target.value ? [e.target.value] : [])}
            aria-label="Filter by request type"
          >
            <option value="">All Request Types</option>
            {filterOptions.requestTypes.map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        )}

        {/* Quarter pills */}
        <div className="quarter-pills" role="group" aria-label="Filter by quarter">
          {QUARTER_PILLS.map(pill => (
            <button
              key={pill.value}
              className={`quarter-pill${quarterFilter === pill.value ? ' active' : ''}`}
              onClick={() => setQuarterFilter(pill.value)}
              type="button"
              aria-pressed={quarterFilter === pill.value}
            >
              {pill.label}
            </button>
          ))}
        </div>

        {/* View toggle */}
        <div className="view-toggle" role="group" aria-label="Switch view">
          <button
            className={`view-toggle-btn${viewMode === 'horizon' ? ' active' : ''}`}
            onClick={() => setViewMode('horizon')}
            type="button"
            aria-pressed={viewMode === 'horizon'}
          >
            <span aria-hidden="true">◈</span> Horizon
          </button>
          <button
            className={`view-toggle-btn${viewMode === 'timeline' ? ' active' : ''}`}
            onClick={() => setViewMode('timeline')}
            type="button"
            aria-pressed={viewMode === 'timeline'}
          >
            <span aria-hidden="true">▦</span> Timeline
          </button>
          <button
            className={`view-toggle-btn${viewMode === 'list' ? ' active' : ''}`}
            onClick={() => setViewMode('list')}
            type="button"
            aria-pressed={viewMode === 'list'}
          >
            <span aria-hidden="true">☰</span> List
          </button>
        </div>
      </div>

      {/* ── Zone 3: Content ── */}
      <div className="roadmap-content">
        {viewMode === 'horizon' ? (
          <HorizonView
            projects={finalProjects}
            onProjectClick={handleProjectClick}
            searchQuery={searchQuery}
            projectId={asanaProjectId}
          />
        ) : viewMode === 'timeline' ? (
          <TimelineView
            projects={finalProjects}
            onProjectClick={handleProjectClick}
          />
        ) : (
          <ListView
            projects={finalProjects}
            onProjectClick={handleProjectClick}
            searchQuery={searchQuery}
          />
        )}
      </div>

      {/* ── Slide-in Detail Panel ── */}
      <ProjectDetailPanel
        project={selectedProject}
        isOpen={selectedProjectGid !== null}
        onClose={handlePanelClose}
      />
    </div>
  );
};

export default StakeholderRoadmap;